import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Globe, Loader2, PlugZap, ExternalLink, ThumbsUp, AlertTriangle, ShieldCheck, Power } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/ui";

export const NOT_CONFIGURED = "Connect Perplexity API key to enable external research.";
let statusPromise = null;
const loadStatus = () => {
  statusPromise = statusPromise || api.get("/research/status").then((r) => r.data).catch(() => { statusPromise = null; return { configured: false, features: {} }; });
  return statusPromise;
};
export const resetResearchStatus = () => { statusPromise = null; };
export const useResearchStatus = () => useLoad(loadStatus);

export const CONF_STYLE = { high: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-400", medium: "bg-amber-500/12 text-amber-600 dark:text-amber-400", low: "bg-slate-500/15 text-slate-600 dark:text-slate-300" };
const TIER = { official: "text-emerald-600 dark:text-emerald-400", established: "text-amber-600 dark:text-amber-400", unverified: "text-red-500", other: "text-muted-foreground" };

export const ConfidenceBadge = ({ c, testId = "confidence-badge" }) => {
  if (!c) return null;
  const lvl = typeof c === "string" ? c : c.level;
  const badge = <span className={`text-[11px] rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${CONF_STYLE[lvl]}`} data-testid={testId}><ShieldCheck className="h-3 w-3" />{lvl} confidence{c.score !== undefined && ` · ${c.score}`}</span>;
  if (typeof c === "string") return badge;
  return (
    <TooltipProvider delayDuration={150}><Tooltip><TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs"><div className="font-semibold mb-1">Source confidence {c.score}/100</div>
        <div>Authority {c.factors?.authority ?? "–"} · Agreement {c.factors?.agreement ?? "–"} · Recency {c.factors?.recency ?? "–"} · Completeness {c.factors?.completeness ?? "–"}</div>
        <ul className="mt-1 list-disc pl-4">{(c.reasons || []).map((r) => <li key={r}>{r}</li>)}</ul>
        {lvl === "low" && <div className="mt-1 font-semibold">Informational only — never triggers actions.</div>}</TooltipContent></Tooltip></TooltipProvider>
  );
};

export const Sources = ({ sources, testId = "research-sources" }) => (sources?.length ? (
  <div className="mt-2" data-testid={testId}><div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">External sources</div>
    <ol className="space-y-0.5 text-xs list-decimal pl-4">{sources.map((s) => <li key={s.url}><a href={s.url} target="_blank" rel="noreferrer noopener" className="text-primary hover:underline inline-flex items-center gap-1 break-all">{s.title}<ExternalLink className="h-3 w-3 shrink-0" /></a>
      {s.tier_label && <span className={`ml-1 text-[10px] ${TIER[s.tier]}`}>· {s.tier_label}</span>}{s.date && <span className="text-muted-foreground"> · {s.date}</span>}</li>)}</ol>
  </div>) : null);

export const ResearchGate = ({ status, feature, testId = "research-not-configured" }) => {
  if (status.data?.configured === false) return <span className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid={testId}><PlugZap className="h-3.5 w-3.5" />{NOT_CONFIGURED}</span>;
  if (status.data && !status.data.features?.[feature]) return <span className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid={`${testId}-off`}><Power className="h-3.5 w-3.5" />Off until validated · <Link to="/app/research-lab" className="text-primary hover:underline">Research validation</Link></span>;
  return null;
};

export const ResearchPanel = ({ path, initial, label, feature, onDone }) => {
  const status = useResearchStatus();
  const [r, setR] = useState(initial || null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = (await api.post(path)).data;
      setR(res);
      onDone?.(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || errMsg(e));
    }
    setBusy(false);
  };
  const ready = status.data?.configured && status.data?.features?.[feature];
  return (
    <div className="rounded-lg border border-border p-3" data-testid="research-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" onClick={run} disabled={busy || !ready} data-testid="research-btn">{busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Globe className="h-3.5 w-3.5 mr-1" />}{r ? "Refresh web research" : label}</Button>
        <ResearchGate status={status} feature={feature} />
        {r && <><ConfidenceBadge c={r.source_confidence || r.confidence} testId="research-confidence" /><span className="text-[11px] text-muted-foreground">Researched {fmtDate(r.at)}</span></>}
      </div>
      {r && <div className="mt-3 text-sm space-y-2" data-testid="research-result">
        {(r.source_confidence?.level || r.confidence) === "low" && <p className="text-xs rounded-md bg-slate-500/10 px-2 py-1" data-testid="research-low-note">Low source confidence — treat these findings as informational, not as grounds for action.</p>}
        <p>{r.summary}</p>
        {r.market && <p className="text-xs"><b>Market:</b> {r.market}</p>}
        {r.positives?.length > 0 && <ul className="text-xs space-y-0.5">{r.positives.map((x) => <li key={x} className="flex gap-1.5"><ThumbsUp className="h-3.5 w-3.5 text-emerald-500 shrink-0" />{x}</li>)}</ul>}
        {r.red_flags?.length > 0 && <ul className="text-xs space-y-0.5" data-testid="research-red-flags">{r.red_flags.map((x) => <li key={x} className="flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />{x}</li>)}</ul>}
        {r.playbook_fit && <p className="text-xs"><b>Playbook fit:</b> {r.playbook_fit}</p>}
        {r.next_action && <p className="text-xs"><b>Next:</b> {r.next_action}</p>}
        <Sources sources={r.sources} />
      </div>}
    </div>
  );
};
